import React, { useMemo } from 'react';

import {
	JiraCoreSiteAri,
	JiraCustomerServiceSiteAri,
	JiraServicedeskSiteAri,
	JiraSoftwareSiteAri,
} from '@atlassian/ari';

import { fg } from '@townsquare/stat-sig/gate';

export type CollaborationContext = {
	hasProjectAndGoalWorkspace: boolean;
	cloudId: string | null;
	userbaseId?: string | null;
	contextId?: string | null;
	orgCloudIds: string[] | null;
	numWorkspaces?: number;
	collabContextScoping?: {
		collabContextCloudIds?: string[];
		isMultiSite?: boolean;
		collabContextSiteAris?: string[];
		hasMultipleUnits?: boolean;
	} | null;
};

export const CollabContext: React.Context<CollaborationContext | null> =
	React.createContext<CollaborationContext | null>(null);

export const useCollabContext = (): CollaborationContext | null => {
	return React.useContext(CollabContext);
};

/**
 * This serves the active cloudId in the current instance. It may be sourced from the URL or the cloudId returned from Bootstrap.
 */
export const useCloudId = (): string | null | undefined => {
	const context = useCollabContext();
	return context?.cloudId;
};

/**
 * Indicates that bootstrap has found an active workspace
 */
export const useProjectsAndGoalsWorkspace = (): boolean => {
	const context = useCollabContext();

	return !!context?.hasProjectAndGoalWorkspace;
};

export const useContextId = (): string | null => {
	const context = useCollabContext();
	return context?.contextId ?? null;
};

const EMPTY_LIST: string[] = [];

export const useCollabContextCloudIds = (): string[] => {
	const context = useCollabContext();

	return context?.collabContextScoping?.collabContextCloudIds ?? context?.orgCloudIds ?? EMPTY_LIST;
};

export const useCollabContextHasJira = (): boolean => {
	const context = useCollabContext();

	const collabContextSiteAris = context?.collabContextScoping?.collabContextSiteAris;

	return useMemo(() => {
		return (
			collabContextSiteAris?.some(
				(ari) =>
					JiraCoreSiteAri.check(ari) ||
					JiraCustomerServiceSiteAri.check(ari) ||
					JiraServicedeskSiteAri.check(ari) ||
					JiraSoftwareSiteAri.check(ari),
			) ?? false
		);
	}, [collabContextSiteAris]);
};

/**
 * cc-units-ga is the global units GA gate owned by the Units team
 */
export const useUnitsEnabled = (): boolean => {
	return fg('cc-units-ga');
};
